import axios from 'axios'

// User-connected MCP servers ("Luca Settings" connectors) live in the support-agent
// backend, not in nocfo-backend. We call it directly with the same minted agent-session
// JWT the chat widget uses (see widgetSession.ts). CORS on the support-agent already
// allows *.nocfo.io.
const SUPPORT_AGENT_ORIGIN = import.meta.env.VITE_SUPPORT_AGENT_API_ORIGIN

export type McpAuthType = 'oauth' | 'token'
// How a token-auth connector collects its secret: a single token, or a client id + client
// secret pair the backend base64-encodes into HTTP Basic auth (Kravia).
export type McpCredential = 'token' | 'basic_pair'
export type McpConnectionStatus = 'connected' | 'pending' | 'needs_reauth' | 'error'

export interface IMcpTemplate {
  key: string
  name: string
  auth_type: McpAuthType
  credential: McpCredential
  icon: string
  description: string
  default_server_url: string | null
  allow_server_url_override: boolean
  allow_auth_type_override: boolean
  docs_url: string | null
  token_field_label: string | null
  default_scopes: string[]
}

export interface IMcpTool {
  name: string
  description: string
  // Standard MCP ToolAnnotations (optional per server): display title + read-only hint.
  title?: string | null
  read_only?: boolean | null
}

export interface IMcpConnection {
  id: string
  template_key: string
  display_name: string
  server_url: string
  auth_type: McpAuthType
  enabled: boolean
  status: McpConnectionStatus
  allowed_tools: string[] | null
  // Tool catalog captured from the server, and the subset the user requires approval for.
  available_tools: IMcpTool[] | null
  approval_tools: string[] | null
  authorization_server: string | null
  created_at: string
  updated_at: string
}

export interface IMcpCreateInput {
  template_key: string
  display_name?: string
  server_url?: string
  auth_type?: McpAuthType
  token?: string
  token_scheme?: string
  client_id?: string
  client_secret?: string
  scopes?: string[]
  allowed_tools?: string[]
  return_url?: string
}

export interface IMcpVerification {
  ok: boolean
  code: 'ok' | 'unauthorized' | 'unreachable'
  tool_count: number | null
}

export interface IMcpCreateResult {
  connection: IMcpConnection
  authorize_url: string | null
  verification: IMcpVerification | null
}

export interface IMcpVerifyResult {
  connection: IMcpConnection
  verification: IMcpVerification
}

export interface IMcpSession {
  token: string | null
  country: string
}

const mcpClient = axios.create({ baseURL: SUPPORT_AGENT_ORIGIN })

const authConfig = ({ token, country }: IMcpSession) => ({
  headers: {
    Authorization: `Bearer ${token ?? ''}`,
    'X-Origin': (country || 'FI').toUpperCase()
  }
})

// A 404 means the feature flag is off on the backend; degrade to an empty list rather
// than surfacing an error, so the page shows a clean empty state.
const readConfig = (session: IMcpSession) => ({
  ...authConfig(session),
  validateStatus: (status: number) => status === 200 || status === 404
})

export const fetchMcpTemplates = async (session: IMcpSession): Promise<IMcpTemplate[]> => {
  const { data, status } = await mcpClient.get('/v1/mcp/templates', readConfig(session))
  return status === 404 ? [] : data.templates
}

export const fetchMcpConnections = async (session: IMcpSession): Promise<IMcpConnection[]> => {
  const { data, status } = await mcpClient.get('/v1/mcp/connections', readConfig(session))
  return status === 404 ? [] : data.connections
}

export const createMcpConnection = async (
  session: IMcpSession,
  input: IMcpCreateInput
): Promise<IMcpCreateResult> => {
  const { data } = await mcpClient.post('/v1/mcp/connections', input, authConfig(session))
  return data
}

export const updateMcpConnection = async (
  session: IMcpSession,
  connectionId: string,
  patch: {
    enabled?: boolean
    display_name?: string
    allowed_tools?: string[]
    clear_allowed_tools?: boolean
    approval_tools?: string[]
  }
): Promise<IMcpConnection> => {
  const { data } = await mcpClient.patch(
    `/v1/mcp/connections/${connectionId}`,
    patch,
    authConfig(session)
  )
  return data
}

export const deleteMcpConnection = async (
  session: IMcpSession,
  connectionId: string
): Promise<void> => {
  await mcpClient.delete(`/v1/mcp/connections/${connectionId}`, authConfig(session))
}

export const verifyMcpConnection = async (
  session: IMcpSession,
  connectionId: string
): Promise<IMcpVerifyResult> => {
  const { data } = await mcpClient.post(
    `/v1/mcp/connections/${connectionId}/verify`,
    {},
    authConfig(session)
  )
  return data
}

export const reauthorizeMcpConnection = async (
  session: IMcpSession,
  connectionId: string,
  body: { return_url?: string; client_id?: string; client_secret?: string; scopes?: string[] }
): Promise<{ authorize_url: string }> => {
  const { data } = await mcpClient.post(
    `/v1/mcp/connections/${connectionId}/authorize`,
    body,
    authConfig(session)
  )
  return data
}
